import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

export interface OrchestratorContext {
  inRouter: boolean;
  fromUrl: boolean; // true only when arrived via Smart Router URL params (active step)
  sessionId: number | null;
  stepIndex: number | null;
  stepKey: string | null;
  subStep: string | null;
  token: string | null;
  returnTo: string | null;
  extraParams: Record<string, string>;
}

const RESERVED = new Set([
  'smartStartSessionId',
  'stepIndex',
  'stepKey',
  'subStep',
  'orchestratorToken',
  'returnTo',
]);

const STORAGE_KEY = 'jarvis_orchestrator_ctx';

interface StoredContext {
  sessionId: number;
  stepIndex: number;
  stepKey: string;
  subStep: string | null;
  token: string;
  returnTo: string | null;
  extraParams: Record<string, string>;
  storedAt?: number; // unix ms — used for expiry
}

const SESSION_TTL_MS = 8 * 60 * 60 * 1000; // 8 hours

function readStored(): StoredContext | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Number.isInteger(parsed?.sessionId) || !Number.isInteger(parsed?.stepIndex)) return null;
    if (!parsed?.stepKey || !parsed?.token) return null;
    // Expire stale sessions so they don't haunt new projects
    if (parsed.storedAt && Date.now() - parsed.storedAt > SESSION_TTL_MS) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed as StoredContext;
  } catch {
    return null;
  }
}

function writeStored(ctx: StoredContext): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ...ctx, storedAt: Date.now() }));
  } catch {
    /* sessionStorage unavailable — fall through, URL params still work */
  }
}

export function clearOrchestratorContext(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    // NOTE: jarvis_orchestrator_guard_applied is intentionally NOT cleared here.
    // It must persist so the guard knows not to auto-switch back after the user
    // manually changes project. Only clearFullOrchestratorContext() clears both.
  } catch {
    /* ignore */
  }
}

// Use this only when fully ending a session (Finish button, session delete).
export function clearFullOrchestratorContext(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem('jarvis_orchestrator_guard_applied');
  } catch {
    /* ignore */
  }
}

export function useOrchestratorContext(): OrchestratorContext {
  const { search } = useLocation();

  return useMemo(() => {
    const params = new URLSearchParams(search);
    const sessionIdStr = params.get('smartStartSessionId');
    const stepIndexStr = params.get('stepIndex');
    const stepKey = params.get('stepKey');
    const token = params.get('orchestratorToken');
    const returnTo = params.get('returnTo');
    const subStep = params.get('subStep');

    const extra: Record<string, string> = {};
    params.forEach((value, key) => {
      if (!RESERVED.has(key)) extra[key] = value;
    });

    const sessionId = sessionIdStr ? parseInt(sessionIdStr, 10) : NaN;
    const stepIndex = stepIndexStr ? parseInt(stepIndexStr, 10) : NaN;
    const fromUrl = Number.isInteger(sessionId)
      && Number.isInteger(stepIndex)
      && !!stepKey
      && !!token;

    if (fromUrl) {
      const resolved: StoredContext = {
        sessionId,
        stepIndex,
        stepKey: stepKey!,
        subStep: subStep || null,
        token: token!,
        returnTo: returnTo || null,
        extraParams: extra,
      };
      writeStored(resolved);
      return { inRouter: true, fromUrl: true, ...resolved };
    }

    const stored = readStored();
    if (stored) {
      return { inRouter: true, fromUrl: false, ...stored };
    }

    return {
      inRouter: false,
      fromUrl: false,
      sessionId: null,
      stepIndex: null,
      stepKey: stepKey || null,
      subStep: subStep || null,
      token: token || null,
      returnTo: returnTo || null,
      extraParams: extra,
    };
  }, [search]);
}
